import { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Search, TrendingDown, TrendingUp, Trash2, AlertTriangle, Filter,
} from 'lucide-react';
import { base44 } from '@/api/base44Client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import CategorySelect from '@/components/shared/CategorySelect';

/**
 * Drill-in view over the transaction list.
 *
 * IMPORTANT — this component always renders RAW transactions straight from the
 * database, never the Dashboard's `filteredTransactions`. The Dashboard
 * rewrites categories for display (several `Transfer - *` categories are shown
 * as "Mortgage Repayments", four shopping categories collapse into
 * "Shopping"). Editing a rewritten row would save the aggregated display label
 * back over the real category and permanently lose the original.
 *
 * The "current view" toggle therefore filters by ID membership rather than
 * reusing the rewritten objects.
 */
export default function TransactionDrillModal({
  transactions,        // RAW, unfiltered — the full transaction list
  visibleIds,          // Set of ids currently shown on the dashboard (optional)
  categories = [],     // known category strings for the picker
  onClose,
}) {
  const [search, setSearch] = useState('');
  // Defaults to the dashboard's current period/category filter. 'all' is an
  // opt-in escape hatch for finding transactions the dashboard excludes
  // (Credit Card Repayments, the Transfer - * categories, other periods).
  const [scope, setScope] = useState(visibleIds ? 'view' : 'all'); // 'view' | 'all'
  const [confirmId, setConfirmId] = useState(null);
  const [localDeleted, setLocalDeleted] = useState(new Set());
  const [openMenuId, setOpenMenuId] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [pendingCategories, setPendingCategories] = useState({}); // optimistic
  const queryClient = useQueryClient();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['transactions'] });
    queryClient.invalidateQueries({ queryKey: ['lastUpload'] });
  };

  const deleteMutation = useMutation({
    mutationFn: (id) => base44.entities.Transaction.delete(id),
    onSuccess: (_, id) => {
      setLocalDeleted(prev => new Set([...prev, id]));
      setConfirmId(null);
      invalidate();
    },
    onError: () => setConfirmId(null),
  });

  const updateCategory = useMutation({
    mutationFn: ({ id, category }) =>
      base44.entities.Transaction.update(id, { category }),
    onMutate: ({ id, category }) => {
      setSavingId(id);
      setPendingCategories(p => ({ ...p, [id]: category }));
    },
    onSuccess: () => invalidate(),
    onError: (_e, { id }) => {
      // Roll the optimistic value back so the UI never claims a save that
      // didn't happen.
      setPendingCategories(p => {
        const next = { ...p };
        delete next[id];
        return next;
      });
    },
    onSettled: () => setSavingId(null),
  });

  const categoryFor = (t) =>
    pendingCategories[t.id] !== undefined ? pendingCategories[t.id] : (t.category || '');

  // Categories offered in the picker: everything known, plus anything set
  // during this session.
  const pickerCategories = useMemo(() => {
    const set = new Set(categories);
    transactions.forEach(t => { if (t.category) set.add(t.category); });
    Object.values(pendingCategories).forEach(v => { if (v) set.add(v); });
    return [...set].sort();
  }, [categories, transactions, pendingCategories]);

  const scoped = useMemo(() => {
    let rows = transactions.filter(t => !localDeleted.has(t.id));
    if (scope === 'view' && visibleIds) {
      rows = rows.filter(t => visibleIds.has(t.id));
    }
    return rows;
  }, [transactions, localDeleted, scope, visibleIds]);

  const filtered = useMemo(() => {
    if (!search) return scoped;
    const q = search.toLowerCase();
    return scoped.filter(t =>
      t.description?.toLowerCase().includes(q) ||
      categoryFor(t).toLowerCase().includes(q)
    );
  }, [scoped, search, pendingCategories]);

  const fmt = (v) =>
    '$' + (v || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const confirmRow = transactions.find(t => t.id === confirmId);
  const editedCount = Object.keys(pendingCategories).length;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl flex flex-col max-h-[88vh]">
        <DialogHeader>
          <DialogTitle className="text-base">
            Transactions
            <span className="ml-2 text-sm font-normal text-slate-400">
              {filtered.length.toLocaleString()}
              {filtered.length !== scoped.length && ` of ${scoped.length.toLocaleString()}`}
              {' shown'}
            </span>
          </DialogTitle>
        </DialogHeader>

        {/* Confirm delete banner */}
        {confirmId && confirmRow && (
          <div className="flex items-start gap-3 p-3 bg-rose-50 border border-rose-200 rounded-lg text-sm">
            <AlertTriangle className="w-4 h-4 text-rose-500 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-rose-800">Delete this transaction?</p>
              <p className="text-rose-600 text-xs mt-0.5 truncate">
                {confirmRow.date} · {confirmRow.description} · {fmt(confirmRow.amount)}
              </p>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <Button
                size="sm" variant="outline" className="h-7 text-xs"
                onClick={() => setConfirmId(null)}
                disabled={deleteMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                className="h-7 text-xs bg-rose-600 hover:bg-rose-700 text-white"
                onClick={() => deleteMutation.mutate(confirmId)}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
              </Button>
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              className="pl-9 h-9 text-sm"
              placeholder="Search by description or category..."
              value={search}
              onChange={e => { setSearch(e.target.value); setOpenMenuId(null); }}
            />
          </div>
          {visibleIds && (
            <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
              {[
                { key: 'view', label: 'Current view' },
                { key: 'all', label: 'All transactions' },
              ].map(s => (
                <button
                  key={s.key}
                  onClick={() => { setScope(s.key); setOpenMenuId(null); }}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all whitespace-nowrap ${
                    scope === s.key ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <p className="text-xs text-slate-400">
          {scope === 'view'
            ? 'Matching the dashboard\u2019s current period and category filters. Categories shown are the stored values, not the aggregated chart labels.'
            : 'Showing every transaction in the database, including categories and periods excluded from the dashboard charts.'}
        </p>

        {/* Table */}
        <div className="flex-1 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="text-center py-10 text-sm text-slate-400">
              <Filter className="w-5 h-5 mx-auto mb-2 text-slate-300" />
              No transactions found
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white border-b border-slate-100 z-10">
                <tr className="text-xs text-slate-400 uppercase tracking-wide">
                  <th className="text-left py-2 px-2 font-medium">Date</th>
                  <th className="text-left py-2 px-2 font-medium">Description</th>
                  <th className="text-left py-2 px-2 font-medium w-56">Category</th>
                  <th className="text-right py-2 px-2 font-medium">Amount</th>
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filtered.map((t, i) => {
                  const isConfirming = confirmId === t.id;
                  const value = categoryFor(t);
                  const edited = pendingCategories[t.id] !== undefined;
                  const saving = savingId === t.id;

                  return (
                    <tr
                      key={t.id || i}
                      className={`transition-colors align-top ${isConfirming ? 'bg-rose-50' : 'hover:bg-slate-50/60'}`}
                    >
                      <td className="py-2 px-2 text-slate-400 whitespace-nowrap text-xs">{t.date}</td>
                      <td className="py-2 px-2 text-slate-700 max-w-[280px]">
                        <span className="line-clamp-2">{t.description}</span>
                      </td>
                      <td className="py-2 px-2">
                        <CategorySelect
                          value={value}
                          categories={pickerCategories}
                          onChange={(v) => {
                            if (v === (t.category || '')) return; // no-op
                            updateCategory.mutate({ id: t.id, category: v });
                          }}
                          isOpen={openMenuId === t.id}
                          onOpenChange={o => setOpenMenuId(o ? t.id : null)}
                          highlight={edited}
                        />
                        {saving && (
                          <span className="text-[10px] text-slate-400 mt-0.5 inline-block">Saving…</span>
                        )}
                      </td>
                      <td className="py-2 px-2 text-right whitespace-nowrap">
                        <span className={`flex items-center justify-end gap-1 font-medium ${t.type === 'debit' ? 'text-rose-600' : 'text-emerald-600'}`}>
                          {t.type === 'debit'
                            ? <TrendingDown className="w-3 h-3" />
                            : <TrendingUp className="w-3 h-3" />}
                          {t.type === 'debit' ? '-' : '+'}{fmt(t.amount)}
                        </span>
                      </td>
                      <td className="py-2 px-1">
                        <button
                          onClick={() => setConfirmId(isConfirming ? null : t.id)}
                          className={`p-1 rounded transition-colors ${
                            isConfirming
                              ? 'text-rose-500 bg-rose-100'
                              : 'text-slate-300 hover:text-rose-400 hover:bg-rose-50'
                          }`}
                          title="Delete transaction"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {(localDeleted.size > 0 || editedCount > 0) && (
          <p className="text-xs text-slate-400 text-center pt-1 border-t border-slate-100">
            {editedCount > 0 && (
              <span className="text-emerald-600 font-medium">
                {editedCount} categor{editedCount === 1 ? 'y' : 'ies'} updated
              </span>
            )}
            {editedCount > 0 && localDeleted.size > 0 && ' · '}
            {localDeleted.size > 0 && `${localDeleted.size} deleted`}
            {' this session'}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
